<template>

  <div class="bg-green-800 text-white w-full z-0">
    <!-- Top bar -->
    <div class="flex items-center justify-between px-3 pt-2 pb-1">
      <!-- Left: Hamburger + Logo -->
      <div class="flex items-center text-2xl gap-2">
        <div @click="toggleSideBar" class="cursor-pointer"><i class="fa-solid fa-bars mr-1"></i></div>
        <div class="font-bold text-xl">amazon</div>
      </div>

      <!-- Center: Search bar (hidden on mobile, shown md+) -->
      <div class="hidden md:flex flex-1 mx-4 max-w-2xl items-center">
        <select
          v-model="selectedCategory"
          class="h-10 px-2 bg-gray-100 text-black border-r border-gray-300 rounded-l-md text-xs focus:outline-none cursor-pointer"
        >
          <option v-for="cat in categories" :key="cat.name" :value="cat.route">
            {{ cat.name }}
          </option>
        </select>
        <input
          type="text"
          v-model="searchQuery"
          @keyup.enter="performSearch"
          class="w-full pl-3 h-10 text-black focus:outline-pink-400"
          placeholder="Search Amazon"
        />
        <div 
          @click="performSearch"
          class="bg-pink-800 h-10 rounded-r-md px-4 flex items-center cursor-pointer hover:bg-pink-900 transition-colors"
        >
          <i class="fa-solid fa-magnifying-glass text-xl"></i>
        </div>
      </div>

      <!-- Right: Sign in + Cart -->
      <div class="flex items-center gap-4">
        <button class="flex items-center gap-1" @click="gotoProfile">
          <div class="font-semibold text-sm hidden sm:block" :class="signinginClass">{{ signingin ? userName : 'Sign in' }}</div>
          <i class="fa-regular fa-user text-2xl"></i>
        </button>
        <div class="relative cursor-pointer" @click="redirectToCart">
          <transition name="bounce">
            <span
              v-if="cartCount > 0"
              key="cartCount"
              class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1 shadow"
            >{{ cartCount }}</span>
          </transition>
          <i class="fa-solid fa-cart-shopping text-3xl"></i>
        </div>
      </div>
    </div>

    <!-- Mobile search bar -->
    <div class="flex md:hidden mx-2 mb-2 items-center">
      <select
        v-model="selectedCategory"
        class="h-10 px-1 bg-gray-100 text-black border-r border-gray-300 rounded-l-md text-xs focus:outline-none cursor-pointer max-w-[6rem]"
      >
        <option v-for="cat in categories" :key="cat.name" :value="cat.route">
          {{ cat.name }}
        </option>
      </select>
      <input
        type="text"
        v-model="searchQuery"
        @keyup.enter="performSearch"
        class="w-full pl-3 h-10 text-black focus:outline-pink-400"
        placeholder="Search Amazon"
      />
      <div 
        @click="performSearch"
        class="bg-pink-800 h-10 rounded-r-md px-4 flex items-center cursor-pointer hover:bg-pink-900 transition-colors"
      >
        <i class="fa-solid fa-magnifying-glass text-xl"></i>
      </div>
    </div>

    <!-- Category nav bar -->
    <div class="flex items-center">
      <div
        class="bg-green-900 flex font-bold overflow-x-auto whitespace-nowrap pl-3 py-2 w-full"
        ref="scrollContainer"
      >
        <div @click="$router.push('/DelightfulDeal')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Deals</div>
        <div @click="$router.push('/Desktop')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Amazon Basics</div>
        <div @click="$router.push('/CustomerLoved')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Best Sellers</div>
        <div @click="$router.push('/VR')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Livestreams</div>
        <div @click="$router.push('/Fragrances')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Music</div>
        <div @click="$router.push('/Book1')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Books</div>
        <div @click="$router.push('/VR')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Video</div>
        <div @click="$router.push('/DelightfulDeal')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">New Releases</div>
        <div @click="$router.push('/HomeDecore')" class="mr-4 inline-block text-sm cursor-pointer hover:underline">Home</div>
        <div @click="$router.push('/Desktop')" class="inline-block text-sm cursor-pointer hover:underline">PC</div>
      </div>
    </div>

    <!-- Delivery location -->
    <div class="flex px-3 py-1 text-sm items-center gap-1">
      <i class="fa-solid fa-location-dot"></i>
      <h1>Deliver to Ethiopia</h1>
      <i class="fa-solid fa-chevron-down text-xs"></i>
    </div>
  </div>

  <!-- Sidebar overlay -->
  <div v-if="showSideBar" class="fixed inset-0 z-50 flex">
    <!-- Sidebar panel -->
    <div class="flex flex-col w-72 sm:w-80 h-full bg-white text-black shadow-2xl overflow-y-auto">
      <!-- Header -->
      <div class="bg-gray-800 text-white px-5 py-4 flex items-center justify-between">
        <div>
          <h1 class="font-bold text-xl">Browse</h1>
          <h1 class="text-3xl font-bold">Amazon</h1>
        </div>
        <div class="text-right">
          <p class="text-sm">{{ signingin ? userName : 'Sign in' }}</p>
          <i class="fa-regular fa-user text-2xl"></i>
        </div>
      </div>

      <!-- Sidebar links -->
      <div class="flex justify-between px-5 py-4 text-lg border-b-2 border-gray-200 cursor-pointer" @click="redirectToHome">
        <a class="font-bold">Amazon Home</a>
        <i class="fa-solid fa-house-user"></i>
      </div>
      <div class="px-5 mt-2 border-b-2 border-gray-200 pb-4 pt-3">
        <a href="/PostProduct">
          <div class="font-bold text-lg">Posting Products</div>
        </a>
        <div class="mt-4 text-gray-600">Movers & Shakers</div>
      </div>
      <div class="px-5 pt-3 pb-4">
        <div class="text-lg font-bold mb-4">Top Departments</div>
        <div class="space-y-4 font-semibold text-gray-800">
          <div @click="$router.push('/HomeDecore')" class="cursor-pointer hover:text-green-700">Home</div>
          <div @click="$router.push('/Beuty')" class="cursor-pointer hover:text-green-700">Health & Household</div>
          <div @click="$router.push('/Book1')" class="cursor-pointer hover:text-green-700">Books</div>
          <div @click="$router.push('/Desktop')" class="cursor-pointer hover:text-green-700">PC</div>
        </div>
        <button class="mt-6 font-semibold text-red-500 hover:underline" @click="logout">Log out</button>

        <div v-if="seeMore" class="mt-4 space-y-0">
          <div v-for="item in extraDepts" :key="item" class="flex justify-between border-t border-gray-200 py-3">
            <div class="font-semibold">{{ item }}</div>
            <i class="fa-solid fa-angle-right text-gray-400"></i>
          </div>
        </div>
        <div @click="toggleShowMore" class="mt-4 font-semibold text-green-700 cursor-pointer">
          {{ seeMore ? 'See less' : 'See all' }} <i class="fa-solid fa-chevron-down ml-1"></i>
        </div>
      </div>
    </div>

    <!-- Overlay backdrop -->
    <div class="flex-1 bg-black bg-opacity-60" @click="toggleSideBar"></div>
  </div>

</template>

<script>
import { useEcommerceStore } from '../stores/Ecommerce';
import { useRouter } from 'vue-router';

export default {
  data() {
    return {
      showSideBar: false,
      seeMore: false,
      store: null,
      EcommerceStore: null,
      userName: '',
      signingin: '',
      searchQuery: '',
      selectedCategory: '/',
      categories: [
        { name: 'All Departments', route: '/' },
        { name: 'Deals', route: '/DelightfulDeal' },
        { name: 'Desktops', route: '/Desktop' },
        { name: 'Laptops', route: '/Laptop' },
        { name: 'Hard Drives', route: '/HardDrive' },
        { name: 'PC Accessories', route: '/PCAccessories' },
        { name: 'Smart Home', route: '/SmartHome' },
        { name: 'Kitchen & Dining', route: '/Kitchen' },
        { name: 'Beauty', route: '/Beuty' },
        { name: 'Books', route: '/Book1' },
        { name: 'Toys', route: '/Toys' },
        { name: 'Xbox', route: '/Xbox' },
        { name: 'Nintendo', route: '/Nintendo' },
        { name: 'PlayStation 5', route: '/Ps5' },
        { name: 'VR', route: '/VR' },
        { name: 'Women\'s Fashion', route: '/Womens' },
        { name: 'Men\'s Fashion', route: '/Mens' },
        { name: 'Girls\' Fashion', route: '/Girls' },
        { name: 'Boys\' Fashion', route: '/Boys' },
        { name: 'Hair Care', route: '/HairCare' },
        { name: 'Skin Care', route: '/SkinCare' },
        { name: 'Fragrances', route: '/Fragrances' },
        { name: 'Makeup', route: '/Makeup' },
        { name: 'Car & Strollers', route: '/Car' },
        { name: 'Travel', route: '/Travel' }
      ],
      extraDepts: [
        'Amazon Music', 'Prime Video', 'Electronics', 'Books',
        'Clothing, Shoes, Jewelry, Watches', 'Electronics', 'Gift Cards'
      ]
    };
  },
  mounted() {
    this.userName = localStorage.getItem('user_name');
    this.signingin = localStorage.getItem('signin');
    this.store = useEcommerceStore();
    this.EcommerceStore = this.store;
  },
  computed: {
    cartCount() {
      return this.store ? this.store.cartNumber : 0;
    },
    signinginClass() {
      return this.signingin ? 'max-w-[11rem] truncate' : '';
    }
  },
  methods: {
    logout() {
      const router = useRouter();
      this.store.logOutUser(router);
      this.$router.push('/Signin');
    },
    gotoProfile() {
      if (this.signingin) {
        this.$router.push('/UserProfile');
      } else {
        this.$router.push('/Signin');
      }
    },
    toggleSideBar() {
      this.showSideBar = !this.showSideBar;
    },
    redirectToHome() {
      this.$router.push('/');
      this.showSideBar = false;
    },
    redirectToCart() {
      this.$router.push('/amazonCart');
    },
    toggleShowMore() {
      this.seeMore = !this.seeMore;
    },
    setupScrollListeners() {
      const scrollContainer = this.$refs.scrollContainer;
      if (!scrollContainer) return;
      scrollContainer.addEventListener('wheel', (evt) => {
        scrollContainer.scrollLeft += evt.deltaY;
      });
    },
    performSearch() {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) {
        this.$router.push(this.selectedCategory);
        return;
      }

      const mapping = {
        laptop: '/Laptop',
        laptops: '/Laptop',
        notebook: '/Laptop',
        notebooks: '/Laptop',
        desktop: '/Desktop',
        desktops: '/Desktop',
        pc: '/Desktop',
        computer: '/Desktop',
        computers: '/Desktop',
        'hard drive': '/HardDrive',
        'hard drives': '/HardDrive',
        ssd: '/HardDrive',
        disk: '/HardDrive',
        accessory: '/PCAccessories',
        accessories: '/PCAccessories',
        keyboard: '/PCAccessories',
        mouse: '/PCAccessories',
        dress: '/BlueDress',
        dresses: '/BlueDress',
        women: '/Womens',
        womens: '/Womens',
        men: '/Mens',
        mens: '/Mens',
        girl: '/Girls',
        girls: '/Girls',
        boy: '/Boys',
        boys: '/Boys',
        book: '/Book1',
        books: '/Book1',
        beauty: '/Beuty',
        cosmetic: '/Beuty',
        cosmetics: '/Beuty',
        lip: '/Beuty',
        cream: '/Beuty',
        toy: '/Toys',
        toys: '/Toys',
        game: '/Toys',
        games: '/Toys',
        xbox: '/Xbox',
        nintendo: '/Nintendo',
        switch: '/Nintendo',
        ps5: '/Ps5',
        playstation: '/Ps5',
        vr: '/VR',
        quest: '/VR',
        smart: '/SmartHome',
        home: '/SmartHome',
        alexa: '/SmartHome',
        kitchen: '/Kitchen',
        dining: '/Dining',
        car: '/Car',
        cars: '/Car',
        stroller: '/Car',
        strollers: '/Car',
        travel: '/Travel',
        bag: '/Travel',
        luggage: '/Travel',
        hair: '/HairCare',
        shampoo: '/HairCare',
        skin: '/SkinCare',
        perfume: '/Fragrances',
        fragrance: '/Fragrances',
        makeup: '/Makeup',
        deal: '/DelightfulDeal',
        deals: '/DelightfulDeal'
      };

      let matchRoute = null;
      for (const key in mapping) {
        if (query.includes(key)) {
          matchRoute = mapping[key];
          break;
        }
      }

      if (matchRoute) {
        this.$router.push(matchRoute);
      } else {
        this.$router.push(this.selectedCategory);
      }
    }
  },
};
</script>

<style scoped>
/* Bounce animation for cart badge */
.bounce-enter-active {
  animation: pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.bounce-leave-active {
  animation: pop 0.2s ease-in reverse;
}
@keyframes pop {
  0%   { transform: scale(0); opacity: 0; }
  70%  { transform: scale(1.3); }
  100% { transform: scale(1); opacity: 1; }
}
</style>
